<template>
  <div>
    <Transition>
      <Text size="xl" class="pb-5" bold> 2. Choose TLFB Variables (Max 3) </Text>
    </Transition>
    <Table
      style="max-width: 800px"
      :headers="[
        'Order',
        { 
          header: 'Variable',
          center: false
        },
        'Mode / Unit',
        {
          header: 'Data Type',
          info: 'Note: Binary (checkbox) inputs will always appear after Numeric Inputs on the calendar survey.',
          center: false
        },
        {
          header: 'Actions',
          center: true
        }
      ]"
    >
      <!-- left off here 12/15. otlfbSurveySubstances dont have records attached here, need to use lookups by id instead -->
      <PendingSubstanceTableRow
        v-for="(oss, i) in otlfbSurveySubstances"
        :key="oss.substanceId + '-' + oss.substanceModeId + '-otlfb-survey-substance-row'"
        :otlfbSurveySubstance="oss"
        :rowIndex="i"
        :animate="i === newSubstanceIndex"
        @delete="deleteRow(i)"
      />
      <TableRow v-if="otlfbSurveySubstances.length < 3">
        <Cell class="w-12">
          <Text size="lg" class="pl-4" bold>
            {{ otlfbSurveySubstances.length + 1 }}
          </Text>
        </Cell>
        <Cell class="w-44">
          <!-- <div class="flex flex-col w-32"> -->
            <Select
              class="w-full"
              @update:modelValue="selectManager.selectSubstance($event)"
              :modelValue="selectManager.selected.substance"
              :disabled="selectManager.isDisabled.substance"
              :placeholder="selectManager.placeHolders.substances"
            >
              <!-- <option disabled>
                {{ selectManager.placeHolders.substances }}
              </option> -->
              <option v-for="s in selectManager.options.substances" :key="s" :value="s">
                {{ toUpperCase(s) }}
              </option>
            </Select>
            <!-- <div v-if="otherSubstanceSelected" class="flex flex-col">
              <div class="flex pt-3">
                <Text size="sm" class="m-1 w-32"> Custom Substance: </Text>
                <input
                  @change="substanceOther = $event.target.value"
                  class="w-24"
                  maxlength="16"
                  :value="substanceOther"
                />
              </div>
              <Text italic size="xs" class="pt-2 text-primary w-48">
                <b>Caution</b>: Instructions not set up for custom substances.
              </Text>
            </div> -->
          <!-- </div> -->
        </Cell>
        <Cell class="w-44">
          <Select
            class="w-full"
            @update:modelValue="selectManager.selectMode($event)"
            :modelValue="selectManager.selected.mode"
            :disabled="selectManager.isDisabled.mode"
            :placeholder="selectManager.placeHolders.modes"
          >
            <option
              v-for="substanceMode in selectManager.options.modes"
              :key="substanceMode"
              :value="substanceMode"
            >
              {{ toUpperCase(substanceMode) }}
            </option>
          </Select>
        </Cell>
        <Cell class="w-44">
          <div class="flex flex-col w-full">
            <Select
              class="w-full"
              :disabled="selectManager.isDisabled.dataType"
              :modelValue="selectManager.selected.dataType"
              :placeholder="selectManager.placeHolders.dataTypes"
              @update:modelValue="selectManager.selectDataType($event)"
            >
              <option
                v-for="dataType in selectManager.options.dataTypes"
                :key="dataType"
              >
                {{ dataType }}
              </option>
            </Select>
          </div>
        </Cell>
        <Cell class="w-8">
          <Button
            @click="submitSelectedSubstances"
            :disabled="!newRowValid"
            stretch
            theme="secondary"
            size="sm"
            >Add
          </Button>
        </Cell>
      </TableRow>
    </Table>
  </div>
  <CustomSubstanceModal
    v-if="showCustomSubstanceModal"
    @close-modal="showCustomSubstanceModal = false"
    @add-custom-substance="addCustomVariableToTable"
    :substanceOptions="substanceOptions"
    :substance="selectManager.selected.substance"
    :substanceMode="selectManager.selected.mode"
    :dataType="selectManager.selected.dataType"
  />
</template>

<script setup>
import { computed, reactive, onMounted, watch, ref } from "vue"
import Text from "./Text.vue"
import Table from "./Table.vue"
import TableRow from "./TableRow.vue"
import Cell from "./Cell.vue"
import Button from "./Button.vue"
import Select from "./Select.vue"
import removeDuplicates from "../utils/removeDuplicates.js"
import PendingSubstanceTableRow from "./PendingSubstanceTableRow.vue"
import CustomSubstanceModal from "./CustomSubstanceModal.vue"
import toUpperCase from "../utils/toUpperCase"

const emit = defineEmits(["update-otlfb-survey-substances", "update-instruction-urls"])

const props = defineProps({
  otlfbSurveySubstances: {
    type: Array,
    required: true
  },
  substanceOptions: {
    type: Array,
    required: true
  }
})

const showCustomSubstanceModal = ref(false)
const newSubstanceIndex = ref(0)
const instructionUrls = ref({})

function useSelectManager(props) {
  const api = reactive({
    placeHolders: {
      substances: "select substance",
      modes: "select mode",
      dataTypes: "select a data type"
    },
    isDisabled: computed(() => ({
      substance: false,
      mode: api.selected.substance === null,
      dataType: api.selected.mode === null
    })),
    options: reactive({
      substances: [],
      modes: [],
      dataTypes: []
    }),
    selected: reactive({
      substance: null,
      mode: null,
      dataType: null
    }),
    clearInputs() {
      api.selected.substance = null
      api.selected.mode = null
      api.selected.dataType = null
      api.resetOptions()
    },
    /**
     * The options available in the various selecte elements are determined
     * in-part by the selections of other elements. It is necessary to
     * reset the options after new selections are made. */
    resetOptions() {
      const substanceNames = props.substanceOptions.map(s => s.name)
      /* "Other" enables free-text custom variables; only append if not already in the catalog. */
      api.options.substances = substanceNames.includes("Other")
        ? substanceNames
        : [...substanceNames, "Other"]
      const substances = !api.selected.substance
        ? props.substanceOptions
        : props.substanceOptions.filter(s => s.name === api.selected.substance)
      
      /* All substanceMode records found on substances, or the substanceModes of the selected substance */
      let modes = substances.map(s => s.substanceModes).flat()
      api.options.modes = api.selected.substance == 'Other'
        ? ['All Types', 'Custom']
        : [...removeDuplicates(modes.map(m => m.name)), 'Custom']

      if (api.selected.mode) {
        modes = modes.filter(m => m.name === api.selected.mode)
      }
      api.options.dataTypes = api.selected.substance == 'Other' || api.selected.mode == 'Custom'
        ? ['Numeric', 'Binary']
        : removeDuplicates(modes.map(m => m.dataType))
      /* Clear any selection made invalid by a change in options */
      if (api.selected.mode && !api.options.modes.includes(api.selected.mode)) {
        api.selected.mode = null
      }
      if (
        api.selected.dataType &&
        !api.options.dataTypes.includes(api.selected.dataType)
      ) {
        api.selected.dataType = null
      }
    },
    selectSubstance(substance) {
      api.selected.substance = substance
      api.resetOptions()
    },
    selectMode(mode) {
      api.selected.mode = mode
      api.resetOptions()
    },
    selectDataType(dataType) {
      api.selected.dataType = dataType
      api.resetOptions()
    }
  })

  return api
}

/** A class-like api for controlling the state and functionality of the interrelated select elements in the table */
const selectManager = useSelectManager(props)

function deleteRow(idx) {
  const newSurveySubstances = [...props.otlfbSurveySubstances]
  newSurveySubstances.splice(idx, 1)
  emit("update-otlfb-survey-substances", newSurveySubstances)
  newSubstanceIndex.value = -1
}

function submitSelectedSubstances() {
  if (selectManager.selected.substance == "Other" || selectManager.selected.mode == 'Custom') {
    showCustomSubstanceModal.value = true
  } else {
    addSurveySubstanceToTable()
  }
}

function addSurveySubstanceToTable() {
  const { mode, dataType, substance: substanceName } = selectManager.selected
  const substance = props.substanceOptions.find(s => s.name === substanceName)
  const substanceMode = substance.substanceModes.find(
    sm => sm.name === mode && sm.dataType === dataType
  )

  const newSurveySubstance = {
    /* otlfbSurveyId will be added when the parent otlfbSurvey is saved for the first time */
    substanceId: substance.id,
    substanceModeId: substanceMode.id,
    substance,
    substanceMode
  }
  
  const substancesCount = props.otlfbSurveySubstances.length
  const numericSubstancesCount = props.otlfbSurveySubstances.filter(s => s.substanceMode.dataType === "Numeric").length
  
  const newSurveySubstances = [...props.otlfbSurveySubstances]
  
  // sort order: numeric before binary
  newSubstanceIndex.value = newSurveySubstance.substanceMode.dataType === "Binary"
    ? substancesCount // binary will always go to end
    : numericSubstancesCount // if numeric, go after already-selected numeric substances
  
  newSurveySubstances.splice(newSubstanceIndex.value, 0, newSurveySubstance)
  
  emit("update-otlfb-survey-substances", newSurveySubstances)

  selectManager.clearInputs()
}

const newRowValid = computed(() => {
  const inputsComplete = Object.values(selectManager.selected).every(
    selection => selection !== null
  )
  if (!inputsComplete) return false

  const { substance, mode, dataType } = selectManager.selected

  const duplicateSurveySubstance = props.otlfbSurveySubstances.find(oss => {
    return (
      oss.substance.name === substance &&
      oss.substanceMode.name === mode
    )
  })

  return !duplicateSurveySubstance
})

async function addCustomVariableToTable(substanceName, mode, dataType, redcapField) {
  
  showCustomSubstanceModal.value = false

  // props.substanceOptions = (await SubstanceApi.all({ include: ["substanceModes"] })).data

  // selectManager.selected.substance = name
  // selectManager.selected.mode = mode  

  // const { mode, dataType, substance: substanceName } = selectManager.selected
  const substance = {name: substanceName}
  const substanceMode = {
    name: mode,
    dataType: dataType,
    redcapFieldName: redcapField
  }

  const newSurveySubstance = {
    substance,
    substanceMode
  }
  
  const substancesCount = props.otlfbSurveySubstances.length
  const numericSubstancesCount = props.otlfbSurveySubstances.filter(s => s.substanceMode.dataType === "Numeric").length
  
  const newSurveySubstances = [...props.otlfbSurveySubstances]
  
  // sort order: numeric before binary
  newSubstanceIndex.value = newSurveySubstance.substanceMode.dataType === "Binary"
    ? substancesCount // binary will always go to end
    : numericSubstancesCount // if numeric, go after already-selected numeric substances
  
  newSurveySubstances.splice(newSubstanceIndex.value, 0, newSurveySubstance)
  
  emit("update-otlfb-survey-substances", newSurveySubstances)

  selectManager.clearInputs()
}


onMounted(() => {
  selectManager.resetOptions()
})

watch(
  () => props.otlfbSurveySubstances,
  () => selectManager.resetOptions()
)

watch(
  () => props.substanceOptions,
  () => selectManager.resetOptions()
)

</script>
